// TypeScript port of api-recorder-extension/src/gherkin-executor.js
// Runs Gherkin API scenarios as real HTTP requests using fetch().
//
// NOTE: Unlike the Chrome extension (which bypasses CORS via host_permissions),
// the portal runs as a regular web app subject to browser CORS enforcement.
// This means fetch() will succeed only for APIs that send correct CORS headers
// (Access-Control-Allow-Origin matching the portal origin, or *).
// For CORS-blocked APIs, the "Run Tests" button will report a fetch error.
// In that case, download the .spec.ts and run it locally with Playwright instead.

import type { ParsedGherkinCase } from './aiOutputParser';
import { parseSteps, parseBackground } from './playwrightConverter';

// ── Result types ──────────────────────────────────────────────────────────────

export interface AssertionResult {
  type: string;
  description: string;
  passed: boolean;
  actual?: string;
  expected?: string;
}

export interface RunResult {
  id: string;
  name: string;
  type: string;
  status: 'passed' | 'failed';
  url: string;
  method: string;
  statusCode: number | null;
  duration: number;
  assertions: AssertionResult[];
  error: string | null;
}

// ── Public API ────────────────────────────────────────────────────────────────

export async function runGherkinScenarios(
  cases: ParsedGherkinCase[],
  rawText: string,
  onResult?: (r: RunResult) => void
): Promise<RunResult[]> {
  const bg = parseBackground(rawText);
  const results: RunResult[] = [];

  for (const tc of cases) {
    const result = await runScenario(tc, bg);
    results.push(result);
    if (onResult) onResult(result);
  }

  return results;
}

// ── Internal: execute one scenario ────────────────────────────────────────────

async function runScenario(
  tc: ParsedGherkinCase,
  bg: { baseURL: string | null; headers: Record<string, string> }
): Promise<RunResult> {
  const steps   = parseSteps(tc.lines);
  const baseURL = steps.baseURL || bg.baseURL || '';
  const headers = { ...bg.headers, ...steps.headers };
  const method  = (steps.method || 'get').toUpperCase();
  const path    = steps.path || '/';
  const url     = path.startsWith('http') ? path : `${baseURL}${path}`;

  const result: RunResult = {
    id:         tc.id,
    name:       tc.scenarioName,
    type:       tc.type,
    status:     'passed',
    url,
    method,
    statusCode: null,
    duration:   0,
    assertions: [],
    error:      null,
  };

  if (!url || url === '/') {
    result.status = 'failed';
    result.error  = 'No URL found in scenario. Ensure "Given the base URL is …" is present.';
    return result;
  }

  const fetchOptions: RequestInit = {
    method,
    headers: { ...headers },
  };

  if (steps.body !== null) {
    try {
      fetchOptions.body = JSON.stringify(JSON.parse(steps.body));
    } catch {
      fetchOptions.body = steps.body;
    }
    const h = fetchOptions.headers as Record<string, string>;
    if (!h['Content-Type'] && !h['content-type']) {
      h['Content-Type'] = 'application/json';
    }
  }

  const start = Date.now();
  try {
    const response = await fetch(url, fetchOptions);
    result.duration   = Date.now() - start;
    result.statusCode = response.status;

    let responseBody: unknown = null;
    try { responseBody = await response.clone().json(); } catch { /* ignore */ }

    for (const assertion of steps.assertions) {
      const ar = evalAssertion(assertion, response, responseBody, result.duration);
      result.assertions.push(ar);
      if (!ar.passed) result.status = 'failed';
    }
  } catch (e: unknown) {
    result.duration = Date.now() - start;
    result.status   = 'failed';
    result.error    = (e instanceof Error) ? e.message : 'Unknown fetch error';
    if (result.error.includes('Failed to fetch') || result.error.includes('NetworkError')) {
      result.error += ' (possible CORS restriction — the target API may not allow browser requests from this origin)';
    }
  }

  return result;
}

// ── Assertion evaluator ───────────────────────────────────────────────────────

function evalAssertion(
  assertion: { type: string; value?: number | string; field?: string; ms?: number; header?: string },
  response: Response,
  body: unknown,
  duration: number
): AssertionResult {
  switch (assertion.type) {

    case 'status':
      return {
        type:        'status',
        description: `Status should be ${assertion.value}`,
        passed:      response.status === assertion.value,
        actual:      String(response.status),
        expected:    String(assertion.value),
      };

    case 'hasField': {
      const val = getNestedField(body, assertion.field ?? '');
      return {
        type:        'hasField',
        description: `Body should contain field "${assertion.field}"`,
        passed:      val !== undefined,
        actual:      val !== undefined ? 'present' : 'missing',
      };
    }

    case 'fieldEquals': {
      const val = getNestedField(body, assertion.field ?? '');
      return {
        type:        'fieldEquals',
        description: `"${assertion.field}" should equal "${assertion.value}"`,
        passed:      String(val) === String(assertion.value),
        actual:      String(val),
        expected:    String(assertion.value),
      };
    }

    case 'emptyArray':
      return {
        type:        'emptyArray',
        description: 'Body should be an empty array',
        passed:      Array.isArray(body) && (body as unknown[]).length === 0,
        actual:      Array.isArray(body) ? `array[${(body as unknown[]).length}]` : typeof body,
      };

    case 'responseTime':
      return {
        type:        'responseTime',
        description: `Response time < ${assertion.ms}ms`,
        passed:      duration < (assertion.ms ?? Infinity),
        actual:      `${duration}ms`,
        expected:    `< ${assertion.ms}ms`,
      };

    case 'responseHeader': {
      const headerVal = response.headers.get(assertion.header ?? '') || '';
      return {
        type:        'responseHeader',
        description: `Header "${assertion.header}" should contain "${assertion.value}"`,
        passed:      headerVal.toLowerCase().includes(String(assertion.value).toLowerCase()),
        actual:      headerVal || '(not present)',
        expected:    String(assertion.value),
      };
    }

    default:
      return { type: assertion.type, description: `Unknown assertion: ${assertion.type}`, passed: true };
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getNestedField(obj: unknown, path: string): unknown {
  if (obj === null || obj === undefined) return undefined;
  const parts = String(path).split('.');
  let cur: unknown = obj;
  for (const part of parts) {
    if (cur === null || cur === undefined) return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  return cur;
}
